import { useState } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { Flex, Icon, Text } from '@nula/core-ui';
import { surfaceDisplayStatus, type SurfaceEntry } from '@/lib/sync/document-surfaces';
import { latestVersionHint, REVIEW_LABELS } from '@/lib/templates/review-copy';
import styles from './surface-detail-face.module.css';

export interface SurfaceRegenerateOffer {
  mode: 'regenerate' | 'review';
  /** Non-null only when the template has a newer version than the surface was made with. */
  latest: { templateName: string } | null;
  busy: boolean;
  refusal: string | null;
  onRegenerate: (useLatestVersion: boolean) => void;
  onReview: () => void;
}

interface SurfaceDetailFaceProps {
  /** The surface these actions act on. */
  entry: SurfaceEntry;
  /** Returns to the surfaces list. */
  onBack: () => void;
  /** Starts an in-place rename on the row; the caller closes this face. */
  onRename: () => void;
  /** Fired only on the confirming second click. While the surface is
   *  generating the same action reads Stop and remove and fires on the first
   *  click: nothing the person wrote is lost (ui-design.md §4.5). */
  onDelete: () => void;
  regenerate?: SurfaceRegenerateOffer;
}

const back = cva(styles.back);
const list = cva(styles.list);
const item = cva(styles.item, {
  variants: {
    tone: { danger: styles.danger, armed: styles.armed },
  },
});

/**
 * A face for one surface's actions, replacing the surfaces list the way the New
 * face does — same back control, same fixed height, so the panel's bottom edge
 * never moves.
 *
 * A face rather than a nested Dropdown: the panel is already `Popover` content,
 * so a second floating layer needs a bespoke z-index tier to clear it, its own
 * focus handling, and somewhere to land near a bottom-left corner that has no
 * room. A face needs none of those, and it does not care how many actions are
 * added later.
 *
 * Move up/down are deliberately absent — reordering is spatial, so it stays
 * inline on the row where the list and the neighbour are both visible.
 */
function RegenerateRows({ offer }: { offer: SurfaceRegenerateOffer }) {
  if (offer.mode === 'review') {
    return (
      <button type="button" role="menuitem" onClick={offer.onReview} className={item()}>
        <Icon name="rate_review" size={18} />
        <Text className={styles.label}>{REVIEW_LABELS.review}</Text>
      </button>
    );
  }
  return (
    <>
      <button
        type="button"
        role="menuitem"
        disabled={offer.busy}
        onClick={() => offer.onRegenerate(false)}
        className={item()}
      >
        <Icon name="refresh" size={18} />
        <Flex vertical className={styles.labels}>
          <Text className={styles.label}>{REVIEW_LABELS.regenerate}</Text>
          <Text size="xs" className={styles.note}>
            {REVIEW_LABELS.regenerateHint}
          </Text>
        </Flex>
      </button>
      {offer.latest && (
        <button
          type="button"
          role="menuitem"
          disabled={offer.busy}
          onClick={() => offer.onRegenerate(true)}
          className={item()}
        >
          <Icon name="new_releases" size={18} />
          <Flex vertical className={styles.labels}>
            <Text className={styles.label}>{REVIEW_LABELS.regenerateLatest}</Text>
            <Text size="xs" className={styles.note}>
              {latestVersionHint(offer.latest.templateName)}
            </Text>
          </Flex>
        </button>
      )}
      {offer.refusal && (
        <Text role="alert" type="secondary" size="xs" className={styles.refusal}>
          {offer.refusal}
        </Text>
      )}
    </>
  );
}

export function SurfaceDetailFace({ entry, onBack, onRename, onDelete, regenerate }: SurfaceDetailFaceProps) {
  // Two-step delete, matching `ConfirmMenuItem`: the first click arms, the
  // second fires. Reset is by unmount — leaving the face disarms it, because
  // the panel remounts this component on the next open.
  const [armed, setArmed] = useState(false);
  const generating = surfaceDisplayStatus(entry) === 'generating';

  return (
    <>
      <button
        type="button"
        role="menuitem"
        aria-label="Back to surfaces"
        onClick={onBack}
        className={back()}
      >
        <Flex align="center" justify="center" className={styles.backIcon} aria-hidden="true">
          <Icon name="arrow_back" size={16} />
        </Flex>
        <Text className={styles.title}>{entry.title}</Text>
      </button>

      <Flex vertical className={list()}>
        <button type="button" role="menuitem" onClick={onRename} className={item()}>
          <Icon name="edit" size={18} />
          <Text className={styles.label}>Rename</Text>
        </button>

        {regenerate && <RegenerateRows offer={regenerate} />}

        <button
          type="button"
          role="menuitem"
          // The armed label alone reads as a bare instruction out of context, so
          // assistive tech gets the surface it would delete.
          aria-label={armed ? `Click to confirm deleting ${entry.title}` : undefined}
          onClick={() => (armed || generating ? onDelete() : setArmed(true))}
          className={clsx(item({ tone: armed ? 'armed' : 'danger' }))}
        >
          <Icon name="delete" size={18} />
          {generating ? (
            <Flex vertical className={styles.labels}>
              <Text className={styles.label}>Stop and remove</Text>
              <Text size="xs" className={styles.note}>
                Still being written. Removing it stops the run.
              </Text>
            </Flex>
          ) : (
            <Text className={styles.label}>{armed ? 'Click to confirm' : 'Delete'}</Text>
          )}
        </button>
      </Flex>
    </>
  );
}
